import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Users, History, Plus, Search, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useApp } from '../contexts/AppContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OwnerDepositManagement = () => {
 const { selectedBranch } = useApp();
 const [loading, setLoading] = useState(true);
 const [owners, setOwners] = useState([]);
 const [report, setReport] = useState([]);
 const [showTopUpModal, setShowTopUpModal] = useState(false);
 const [togglingId, setTogglingId] = useState(null);
 // 2026-06-06: Edit Saldo modal — set balance manual + catat adjustment.
 const [showEditModal, setShowEditModal] = useState(false);
 const [editOwner, setEditOwner] = useState(null);
 const [editBalance, setEditBalance] = useState('');
 const [editDescription, setEditDescription] = useState('');
 const [editSaving, setEditSaving] = useState(false);

 const handleToggleEnforce = async (owner) => {
 if (!owner?.id) return;
 setTogglingId(owner.id);
 const desired = !owner.deposit_enforced;
 try {
 const token = localStorage.getItem('token');
 const resp = await fetch(`${API_URL}/api/v2/owner-deposits/${owner.id}/toggle-enforce`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
 body: JSON.stringify({ enabled: desired }),
 });
 if (!resp.ok) {
 const err = await resp.json().catch(() => ({}));
 throw new Error(err.detail || 'Gagal mengubah mode deposit');
 }
 // Refresh laporan setelah toggle sukses
 await fetchData();
 } catch (e) {
 alert(e.message || 'Gagal mengubah mode deposit');
 } finally {
 setTogglingId(null);
 }
 };

 const [selectedOwner, setSelectedOwner] = useState(null);
 const [topUpAmount, setTopUpAmount] = useState('');
 const [topUpNotes, setTopUpNotes] = useState('');
 const [processing, setProcessing] = useState(false);
 const [searchTerm, setSearchTerm] = useState('');

 useEffect(() => {
 fetchData();
 }, [selectedBranch]);

 const fetchData = async () => {
 try {
 setLoading(true);
 const token = localStorage.getItem('token');
 const branchParam = selectedBranch && selectedBranch !== 'all' ? `?branch_id=${selectedBranch}` : '';
 
 // Fetch report data
 const reportRes = await axios.get(`${API_URL}/api/v2/owner-deposits/report${branchParam}`, {
 headers: { Authorization: `Bearer ${token}` }
 });
 
 if (reportRes.data.success) {
 setReport(reportRes.data.data);
 }
 
 // Fetch owners list
 const ownersRes = await axios.get(`${API_URL}/api/v2/owners${branchParam}`, {
 headers: { Authorization: `Bearer ${token}` }
 });
 
 if (ownersRes.data.success) {
 setOwners(ownersRes.data.data);
 }
 
 } catch (error) {
 console.error('Error fetching data:', error);
 toast.error('Gagal memuat data');
 } finally {
 setLoading(false);
 }
 };

 const handleTopUp = async () => {
 if (!selectedOwner || !topUpAmount || parseFloat(topUpAmount) <= 0) {
 toast.error('Pilih owner dan masukkan jumlah deposit yang valid');
 return;
 }

 try {
 setProcessing(true);
 const token = localStorage.getItem('token');
 
 const response = await axios.post(
 `${API_URL}/api/v2/owner-deposits/topup`,
 {
 owner_id: selectedOwner.id,
 amount: parseFloat(topUpAmount),
 description: topUpNotes
 },
 { headers: { Authorization: `Bearer ${token}` } }
 );

 if (response.data.success) {
 toast.success('✅ Deposit berhasil ditambahkan! Notifikasi WA telah dikirim.');
 setShowTopUpModal(false);
 setSelectedOwner(null);
 setTopUpAmount('');
 setTopUpNotes('');
 fetchData(); // Refresh data
 }
 } catch (error) {
 console.error('Error top up:', error);
 toast.error(error.response?.data?.detail || 'Gagal menambahkan deposit');
 } finally {
 setProcessing(false);
 }
 };

 const filteredReport = report.filter(item =>
 (item.name || '').toLowerCase().includes(searchTerm.toLowerCase())
 );

 const totalStats = {
 totalOwners: report.length,
 totalDeposits: report.reduce((sum, item) => sum + (item.total_topup || 0), 0),
 totalBalance: report.reduce((sum, item) => sum + (item.balance || 0), 0),
 totalUsage: report.reduce((sum, item) => sum + (item.total_usage || 0), 0)
 };

 if (loading) {
 return (
 <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-64">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
 <p className="text-gray-600 dark:text-gray-300">Memuat data...</p>
 </div>
 </div>
 );
 }

 return (
 <div className="p-6 space-y-6 ">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Manajemen Deposit Owner</h1>
 <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
 Kelola deposit dan saldo owner untuk penagihan pelanggan
 </p>
 </div>
 <Button
 onClick={() => setShowTopUpModal(true)}
 variant="outline"
 className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
 >
 <Plus className="h-4 w-4" />
 Top Up Deposit
 </Button>
 </div>

 {/* Stats Cards */}
 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
 <Card className="bg-white dark:bg-slate-800">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-gray-600 dark:text-gray-400">Total Owner</p>
 <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalStats.totalOwners}</p>
 </div>
 <div className="p-3 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
 <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
 </div>
 </div>
 </CardContent>
 </Card>

 <Card className="bg-white dark:bg-slate-800">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-gray-600 dark:text-gray-400">Total Deposit</p>
 <p className="text-2xl font-bold text-gray-900 dark:text-white">
 Rp {totalStats.totalDeposits.toLocaleString('id-ID')}
 </p>
 </div>
 <div className="p-3 bg-green-100 dark:bg-green-900/50 rounded-lg">
 <TrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
 </div>
 </div>
 </CardContent>
 </Card>

 <Card className="bg-white dark:bg-slate-800">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-gray-600 dark:text-gray-400">Saldo Tersedia</p>
 <p className="text-2xl font-bold text-gray-900 dark:text-white">
 Rp {totalStats.totalBalance.toLocaleString('id-ID')}
 </p>
 </div>
 <div className="p-3 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
 <DollarSign className="h-6 w-6 text-purple-600 dark:text-purple-400" />
 </div>
 </div>
 </CardContent>
 </Card>

 <Card className="bg-white dark:bg-slate-800">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-gray-600 dark:text-gray-400">Total Penggunaan</p>
 <p className="text-2xl font-bold text-gray-900 dark:text-white">
 Rp {totalStats.totalUsage.toLocaleString('id-ID')}
 </p>
 </div>
 <div className="p-3 bg-orange-100 dark:bg-orange-900/50 rounded-lg">
 <History className="h-6 w-6 text-orange-600 dark:text-orange-400" />
 </div>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Owner Report Table */}
 <Card className="bg-white dark:bg-slate-800">
 <CardHeader>
 <div className="flex items-center justify-between">
 <CardTitle>Laporan Deposit per Owner</CardTitle>
 <div className="relative w-64">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
 <Input
 placeholder="Cari owner..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="pl-10"
 />
 </div>
 </div>
 </CardHeader>
 <CardContent>
 <div className="overflow-x-auto">
 <table className="w-full text-xs">
 <thead>
 <tr className="border-b border-slate-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700">
 <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-slate-200">Owner</th>
 <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-slate-200">Telepon</th>
 <th className="text-right py-2 px-3 font-semibold text-gray-700 dark:text-slate-200">Saldo</th>
 <th className="text-center py-2 px-3 font-semibold text-gray-700 dark:text-slate-200" title="Enable: butuh saldo cukup untuk transaksi. Disable: unlimited.">Mode Deposit</th>
 <th className="text-right py-2 px-3 font-semibold text-gray-700 dark:text-slate-200">Fee</th>
 <th className="text-right py-2 px-3 font-semibold text-gray-700 dark:text-slate-200">Total Deposit</th>
 <th className="text-right py-2 px-3 font-semibold text-gray-700 dark:text-slate-200">Total Pakai</th>
 <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-slate-200">Last Top Up</th>
 <th className="text-center py-2 px-3 font-semibold text-gray-700 dark:text-slate-200">Aksi</th>
 </tr>
 </thead>
 <tbody>
 {filteredReport.length === 0 ? (
 <tr>
 <td colSpan="9" className="text-center p-8 text-gray-500 dark:text-gray-400">
 Tidak ada data
 </td>
 </tr>
 ) : (
 filteredReport.map((item) => (
 <tr key={item.id} className="border-b border-slate-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700">
 <td className="py-2 px-3">
 <div className="font-medium text-gray-900 dark:text-white">{item.name}</div>
 <div className="text-xs text-gray-500 dark:text-gray-400">{item.owner_code || '-'}</div>
 </td>
 <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{item.phone || '-'}</td>
 <td className="py-2 px-3 text-right">
 <span className="font-semibold text-green-600 dark:text-green-400">
 Rp {(item.balance || 0).toLocaleString('id-ID')}
 </span>
 </td>
 <td className="py-2 px-3 text-center">
 <button
 type="button"
 onClick={() => handleToggleEnforce(item)}
 data-testid={`toggle-deposit-enforce-${item.id}`}
 disabled={togglingId === item.id}
 title={item.deposit_enforced ? 'Klik untuk DISABLE — transaksi unlimited' : 'Klik untuk ENABLE — transaksi butuh saldo cukup'}
 className={
 "relative inline-flex h-5 w-10 items-center rounded-full transition-colors disabled:opacity-50 " +
 (item.deposit_enforced ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-slate-600')
 }
 >
 <span
 className={
 "inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform " +
 (item.deposit_enforced ? 'translate-x-5' : 'translate-x-1')
 }
 />
 </button>
 <div className={"mt-1 text-[10px] font-medium " + (item.deposit_enforced ? 'text-emerald-600' : 'text-gray-500')}>
 {item.deposit_enforced ? 'Enable' : 'Disable'}
 </div>
 </td>
 <td className="py-2 px-3 text-right">
 <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300">
 -
 </span>
 </td>
 <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">
 Rp {(item.total_topup || 0).toLocaleString('id-ID')}
 </td>
 <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">
 Rp {(item.total_usage || 0).toLocaleString('id-ID')}
 </td>
 <td className="py-2 px-3 text-xs text-gray-600 dark:text-gray-300 align-top">

  {item.last_topup ? (

   <div className="space-y-1">

    <div className="font-medium">{new Date(item.last_topup).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>

    {item.topup_history && item.topup_history.length > 0 && (

     <div className="flex flex-wrap gap-1 max-w-[220px]" title={item.topup_history.map(h => `${new Date(h.created_at).toLocaleDateString('id-ID')}: Rp ${(h.amount || 0).toLocaleString('id-ID')}`).join('\n')}>

      {item.topup_history.slice(0, 5).map((h, idx) => (

       <span key={idx} className="inline-flex px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 whitespace-nowrap">

        +Rp {((h.amount || 0) / 1000).toFixed(0)}k

       </span>

      ))}

      {item.topup_history.length > 5 && (

       <span className="text-[10px] text-gray-500">+{item.topup_history.length - 5} lagi</span>

      )}

     </div>

    )}

   </div>

  ) : '-'}

 </td>
 <td className="py-2 px-3 text-center">
 <div className="inline-flex items-center gap-1.5">
 <Button
 size="sm"
 variant="outline"
 onClick={() => {
 setSelectedOwner(item);
 setShowTopUpModal(true);
 }}
 data-testid={`topup-btn-${item.id}`}
 className="h-7 text-xs px-2"
 >
 Top Up
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => {
 setEditOwner(item);
 setEditBalance(String(item.balance || 0));
 setEditDescription('');
 setShowEditModal(true);
 }}
 data-testid={`edit-balance-btn-${item.id}`}
 title="Edit saldo manual (adjustment)"
 className="h-7 px-2 border-amber-300 text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-900/30"
 >
 <Pencil className="w-3 h-3" />
 </Button>
 </div>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>
 </CardContent>
 </Card>

 {/* Top Up Modal */}
 {showTopUpModal && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className="bg-white dark:bg-slate-800 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
 <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-white">Top Up Deposit Owner</h3>
 
 <div className="space-y-3">
 {/* Owner Selection */}
 <div>
 <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">
 Pilih Owner
 </label>
 <select
 value={selectedOwner?.id != null ? String(selectedOwner.id) : ''}
 onChange={(e) => {
 // 2026-06-06 FIX: owner.id dari API adalah number,
 // sedangkan e.target.value adalah string. Strict === gagal.
 const owner = owners.find(o => String(o.id) === String(e.target.value));
 setSelectedOwner(owner || null);
 }}
 className="w-full px-3 py-2 text-sm h-9 border border-gray-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-700 dark:text-white"
 >
 <option value="">-- Pilih Owner --</option>
 {owners.map(owner => (
 <option key={owner.id} value={owner.id}>
 {owner.name} - Saldo: Rp {(owner.balance || 0).toLocaleString('id-ID')} | Fee: {
 owner.fee_type === 'fixed' 
 ? `Rp ${(owner.fee_value || 0).toLocaleString('id-ID')}`
 : `${owner.fee_value || owner.fee_percentage || 0}%`
 }
 </option>
 ))}
 </select>
 </div>

 {/* Amount */}
 <div>
 <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">
 Jumlah Deposit (Rp)
 </label>
 <Input
 type="number"
 placeholder="100000"
 value={topUpAmount}
 onChange={(e) => setTopUpAmount(e.target.value)}
 min="0"
 className="text-sm h-9"
 />
 </div>

 {/* Notes */}
 <div>
 <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">
 Catatan (Optional)
 </label>
 <textarea
 value={topUpNotes}
 onChange={(e) => setTopUpNotes(e.target.value)}
 placeholder="Contoh: Top up bulanan Desember"
 className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-700 dark:text-white"
 rows="2"
 />
 </div>

 {/* Owner Info & Preview */}
 {selectedOwner && (
 <div className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg p-2.5 space-y-1.5">
 <p className="text-xs font-semibold text-gray-700 dark:text-slate-200">
 Info Owner:
 </p>
 <div className="grid grid-cols-2 gap-2 text-xs">
 <div>
 <span className="text-gray-600 dark:text-gray-300">Saldo Saat Ini:</span>
 <span className="ml-2 font-semibold text-green-600 dark:text-green-400">
 Rp {(selectedOwner.balance || 0).toLocaleString('id-ID')}
 </span>
 </div>
 <div>
 <span className="text-gray-600 dark:text-gray-300">Fee Owner:</span>
 <span className="ml-2 font-semibold text-blue-600 dark:text-blue-400">
 {selectedOwner.fee_type === 'fixed' 
 ? `Rp ${(selectedOwner.fee_value || 0).toLocaleString('id-ID')}`
 : `${selectedOwner.fee_value || selectedOwner.fee_percentage || 0}%`
 }
 </span>
 </div>
 </div>
 {topUpAmount && (
 <div className="mt-1.5 pt-1.5 border-t border-gray-300 dark:border-slate-600">
 <p className="text-xs text-blue-900">
 <strong>Setelah Top Up:</strong> Saldo menjadi{' '}
 <strong className="text-green-600 dark:text-green-400">
 Rp {((selectedOwner.balance || 0) + parseFloat(topUpAmount || 0)).toLocaleString('id-ID')}
 </strong>
 </p>
 </div>
 )}
 </div>
 )}
 </div>

 {/* Actions */}
 <div className="flex gap-2 pt-3 border-t mt-4">
 <Button
 variant="outline"
 onClick={() => {
 setShowTopUpModal(false);
 setSelectedOwner(null);
 setTopUpAmount('');
 setTopUpNotes('');
 }}
 className="flex-1 h-9 text-sm"
 disabled={processing}
 >
 Batal
 </Button>
 <Button
 onClick={handleTopUp}
 disabled={processing || !selectedOwner || !topUpAmount}
 className="flex-1 h-9 text-sm bg-indigo-600 hover:bg-indigo-700"
 >
 {processing ? 'Memproses...' : 'Top Up Sekarang'}
 </Button>
 </div>
 </div>
 </div>
 )}
 {/* Edit Saldo Modal */}
 {showEditModal && editOwner && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className="bg-white dark:bg-slate-800 rounded-lg max-w-md w-full p-6 shadow-2xl">
 <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-white flex items-center gap-2">
 <Pencil className="w-4 h-4 text-amber-600 dark:text-amber-400" />
 Edit Saldo Owner
 </h3>
 <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 dark:bg-amber-900/30">
 <div><strong>{editOwner.name}</strong> ({editOwner.owner_code})</div>
 <div className="mt-1">Saldo saat ini: <strong>Rp {(editOwner.balance || 0).toLocaleString('id-ID')}</strong></div>
 <div className="mt-1 italic">Selisih akan dicatat sebagai &quot;Adjustment&quot; di laporan deposit.</div>
 </div>
 <div className="space-y-3">
 <div>
 <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">
 Saldo Baru (Rp)
 </label>
 <Input
 type="number"
 value={editBalance}
 onChange={(e) => setEditBalance(e.target.value)}
 min="0"
 data-testid="edit-balance-input"
 className="text-sm h-9"
 autoFocus
 />
 {editBalance !== '' && (
 <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
 {Number(editBalance) - Number(editOwner.balance || 0) >= 0 ? '+' : ''}
 Rp {(Number(editBalance) - Number(editOwner.balance || 0)).toLocaleString('id-ID')} dari saldo saat ini
 </p>
 )}
 </div>
 <div>
 <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">
 Catatan (Optional)
 </label>
 <textarea
 value={editDescription}
 onChange={(e) => setEditDescription(e.target.value)}
 placeholder="Contoh: koreksi setor manual via transfer bank"
 rows={2}
 data-testid="edit-balance-description"
 className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-700 dark:text-white"
 />
 </div>
 </div>
 <div className="mt-4 flex gap-2">
 <Button
 type="button"
 variant="outline"
 onClick={() => { setShowEditModal(false); setEditOwner(null); }}
 disabled={editSaving}
 className="flex-1"
 >
 Batal
 </Button>
 <Button
 type="button"
 disabled={editSaving || editBalance === '' || Number(editBalance) < 0}
 data-testid="edit-balance-save-btn"
 onClick={async () => {
 setEditSaving(true);
 try {
 const token = localStorage.getItem('token');
 const resp = await fetch(`${API_URL}/api/v2/owner-deposits/${editOwner.id}/adjust`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
 body: JSON.stringify({ balance: Number(editBalance), description: editDescription }),
 });
 const data = await resp.json();
 if (!resp.ok) throw new Error(data.detail || 'Gagal update saldo');
 toast.success(data.message || 'Saldo berhasil di-update');
 setShowEditModal(false);
 setEditOwner(null);
 fetchData();
 } catch (e) {
 toast.error(e.message || 'Gagal update saldo');
 } finally {
 setEditSaving(false);
 }
 }}
 className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
 >
 {editSaving ? 'Menyimpan...' : 'Simpan Saldo'}
 </Button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default OwnerDepositManagement;
